// src/config/resourceConfig.tsx
import React from 'react';
import {
    CorporateFare,
    People as UserIcon,
    Hotel as HotelIcon,
    ReceiptLong as TagIcon,
    Router as ControllerIcon,
    Tablet as HMIIcon,
    Apps as LockerIcon,
    ViewComfy as LockerTypeIcon,
    AssignmentTurnedIn as AssignmentIcon,
    Settings as SettingsIcon,
    DevicesOther as DevicesIcon,
} from '@mui/icons-material';

import { getCurrentUser } from '../utils/auth';

// Lazy-loaded feature components (code splitting)
const GroupList = React.lazy(() => import('../features/groups').then(m => ({ default: m.GroupList })));
const GroupCreate = React.lazy(() => import('../features/groups').then(m => ({ default: m.GroupCreate })));
const GroupEdit = React.lazy(() => import('../features/groups').then(m => ({ default: m.GroupEdit })));

const UserList = React.lazy(() => import('../features/users').then(m => ({ default: m.UserList })));
const UserCreate = React.lazy(() => import('../features/users').then(m => ({ default: m.UserCreate })));
const UserEdit = React.lazy(() => import('../features/users').then(m => ({ default: m.UserEdit })));

const HotelList = React.lazy(() => import('../features/hotels').then(m => ({ default: m.HotelList })));
const HotelCreate = React.lazy(() => import('../features/hotels').then(m => ({ default: m.HotelCreate })));
const HotelEdit = React.lazy(() => import('../features/hotels').then(m => ({ default: m.HotelEdit })));

const TagTypeList = React.lazy(() => import('../features/tag-types').then(m => ({ default: m.TagTypeList })));
const TagTypeCreate = React.lazy(() => import('../features/tag-types').then(m => ({ default: m.TagTypeCreate })));
const TagTypeEdit = React.lazy(() => import('../features/tag-types').then(m => ({ default: m.TagTypeEdit })));

const LockerControllerList = React.lazy(() => import('../features/locker-controllers').then(m => ({ default: m.LockerControllerList })));
const LockerControllerCreate = React.lazy(() => import('../features/locker-controllers').then(m => ({ default: m.LockerControllerCreate })));
const LockerControllerEdit = React.lazy(() => import('../features/locker-controllers').then(m => ({ default: m.LockerControllerEdit })));

const HMIList = React.lazy(() => import('../features/hmis').then(m => ({ default: m.HMIList })));
const HMICreate = React.lazy(() => import('../features/hmis').then(m => ({ default: m.HMICreate })));
const HMIEdit = React.lazy(() => import('../features/hmis').then(m => ({ default: m.HMIEdit })));

const HmiStaffList = React.lazy(() => import('../features/hmi-staff').then(m => ({ default: m.HmiStaffList })));
const HmiStaffCreate = React.lazy(() => import('../features/hmi-staff').then(m => ({ default: m.HmiStaffCreate })));
const HmiStaffEdit = React.lazy(() => import('../features/hmi-staff').then(m => ({ default: m.HmiStaffEdit })));

const LockerList = React.lazy(() => import('../features/lockers').then(m => ({ default: m.LockerList })));
const LockerCreate = React.lazy(() => import('../features/lockers').then(m => ({ default: m.LockerCreate })));
const LockerEdit = React.lazy(() => import('../features/lockers').then(m => ({ default: m.LockerEdit })));

const LockerTypeList = React.lazy(() => import('../features/locker-types').then(m => ({ default: m.LockerTypeList })));
const LockerTypeCreate = React.lazy(() => import('../features/locker-types').then(m => ({ default: m.LockerTypeCreate })));
const LockerTypeEdit = React.lazy(() => import('../features/locker-types').then(m => ({ default: m.LockerTypeEdit })));

const LockerAssignmentList = React.lazy(() => import('../features/locker-assignments').then(m => ({ default: m.LockerAssignmentList })));
const LockerAssignmentCreate = React.lazy(() => import('../features/locker-assignments').then(m => ({ default: m.LockerAssignmentCreate })));
const LockerAssignmentEdit = React.lazy(() => import('../features/locker-assignments').then(m => ({ default: m.LockerAssignmentEdit })));

const HMISimulator = React.lazy(() => import('../features/hmi-simulator').then(m => ({ default: m.HMISimulator })));

const AdminOptionsPage = React.lazy(() => import('../features/admin-options').then(m => ({ default: m.AdminOptionsPage })));
const DevicesPage = React.lazy(() => import('../features/admin-options').then(m => ({ default: m.DevicesPage })));


// Función mejorada para verificar permisos
const hasResourcePermission = (resource: string, action: string): boolean => {
    try {
        // Obtener el rol actual del usuario
        const user = getCurrentUser();
        const currentRole = user?.role || localStorage.getItem('role');
        
        if (!currentRole) {
            console.warn('No role found for permission check');
            return false;
        }
        
        // Obtener la configuración del recurso
        const resourceConfig = resourcesConfig[resource];
        if (!resourceConfig) {
            console.warn(`Resource ${resource} not found in config`);
            return false;
        }
        
        // Verificar permisos
        const permissions = resourceConfig.permissions;
        const allowedRoles = permissions[action];
        
        if (!allowedRoles) {
            console.warn(`Action ${action} not found for resource ${resource}`);
            return false;
        }
        
        const hasPermission = allowedRoles.includes(currentRole);

        return hasPermission;
    } catch (error) {
        console.error('Error checking permissions:', error);
        return false;
    }
};

// Función para obtener elementos del menú
export const getMenuItems = () => {
    const menuItems = [];
    const user = getCurrentUser();
    const currentRole = user?.role || localStorage.getItem('role');
    
    if (!currentRole) {
        return [];
    }
    
    // Iterar sobre los recursos y agregar solo los que tienen permisos
    Object.values(resourcesConfig).forEach((resource: any) => {
        if (resource.showInMenu && hasResourcePermission(resource.name, 'access')) {
            menuItems.push({
                name: resource.name,
                textKey: resource.label,
                icon: resource.icon ? React.createElement(resource.icon) : null,
                route: resource.route,
                order: resource.menuOrder || 99,
            });
        }
    });
    
    // Ordenar por menuOrder
    return menuItems.sort((a, b) => a.order - b.order);
};

// Función principal para React Admin
export const getResourcesForReactAdmin = () => {
    const user = getCurrentUser();
    const currentRole = user?.role || localStorage.getItem('role');
    
    
    if (!currentRole) {
        console.warn('No role found, returning empty resources');
        return [];
    }
    
    // Convertir recursos a formato React Admin
    const resources = Object.values(resourcesConfig)
        .filter((resource: any) => {
            // Solo incluir recursos a los que el usuario tiene acceso
            const hasAccess = hasResourcePermission(resource.name, 'access');
            return hasAccess;
        })
        .map((resource: any) => ({
            name: resource.name,
            list: hasResourcePermission(resource.name, 'list') && resource.components.list
                ? resource.components.list 
                : undefined,
            create: hasResourcePermission(resource.name, 'create') && resource.components.create
                ? resource.components.create 
                : undefined,
            edit: hasResourcePermission(resource.name, 'edit') && resource.components.edit
                ? resource.components.edit 
                : undefined,
            show: hasResourcePermission(resource.name, 'show') && resource.components.show
                ? resource.components.show 
                : undefined,
            options: { label: resource.label },
            recordRepresentation: resource.recordRepresentation,
        }))
        .filter(resource => 
            // Solo incluir recursos que tienen al menos un componente válido
            resource.list || resource.create || resource.edit || resource.show
        );
    
    return resources;
};

// Configuración de recursos
export const resourcesConfig = {
    groups: {
        name: 'groups',
        recordRepresentation: 'name',
        route: '/groups',
        label: 'resources.groups.name',
        icon: CorporateFare,
        permissions: {
            access: ['SuperAdmin'],
            list: ['SuperAdmin'],
            create: ['SuperAdmin'],
            edit: ['SuperAdmin'],
            delete: ['SuperAdmin'],
            show:[]
        },
        components: {
            list: GroupList,
            create: GroupCreate,
            edit: GroupEdit,
            show: undefined,
        },
        showInMenu: false, // Grouped under admin-options
        menuOrder: 1
    },

    users: {
        name: 'users',
        recordRepresentation: 'name', 
        route: '/users',
        label: 'resources.users.name',
        icon: UserIcon, 
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            show:[]
        },
        components: {
            list: UserList,
            create: UserCreate,
            edit: UserEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 2
    },

    hotels: {
        name: 'hotels',
        recordRepresentation: 'name',
        route: '/hotels',
        label: 'resources.hotels.name',
        icon: HotelIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin'],
            list: ['SuperAdmin', 'GroupAdmin'],
            create: ['SuperAdmin', 'GroupAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin'],
            delete: ['SuperAdmin'],
            manageZones: ['SuperAdmin', 'GroupAdmin'],
            manageTexts: ['SuperAdmin', 'GroupAdmin'],
            manageNotifications: ['SuperAdmin', 'GroupAdmin'],
            manageFields: ['SuperAdmin', 'GroupAdmin'],
            show:[]
        },
        components: {
            list: HotelList,
            create: HotelCreate,
            edit: HotelEdit,
            show: undefined,
        },
        showInMenu: false, // Grouped under admin-options
        menuOrder: 3,
    },

    'tag-types': {
        name: 'tag-types',
        recordRepresentation: 'name',
        route: '/tag-types',
        label: 'resources.tag-types.name',
        icon: TagIcon,
        permissions: {
            access: ['SuperAdmin'],
            list: ['SuperAdmin'],
            create: ['SuperAdmin'],
            edit: ['SuperAdmin'],
            delete: ['SuperAdmin'],
            show:[]
        },
        components: {
            list: TagTypeList,
            create: TagTypeCreate,
            edit: TagTypeEdit,
            show: undefined,
        },
        showInMenu: false, // Grouped under admin-options
        menuOrder: 4
    },

    'locker-controllers': {
        name: 'locker-controllers',
        recordRepresentation: 'name',
        route: '/locker-controllers',
        label: 'resources.locker-controllers.name',
        icon: ControllerIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin'],
            list: ['SuperAdmin', 'GroupAdmin'],
            create: ['SuperAdmin', 'GroupAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin'],
            delete: ['SuperAdmin'],
            show:[]
        },
        components: {
            list: LockerControllerList,
            create: LockerControllerCreate,
            edit: LockerControllerEdit,
            show: undefined,
        },
        showInMenu: false, // Grouped under devices
        menuOrder: 5
    },

    hmis: {
        name: 'hmis',
        recordRepresentation: 'name',
        route: '/hmis',
        label: 'resources.hmis.name',
        icon: HMIIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            show:[]
        },
        components: {
            list: HMIList,
            create: HMICreate,
            edit: HMIEdit,
            show: undefined,
        },
        showInMenu: false, // Grouped under devices
        menuOrder: 6
    },

    'hmi-staff': {
        name: 'hmi-staff',
        recordRepresentation: 'name',
        route: '/hmi-staff',
        label: 'resources.hmi-staff.name',
        icon: UserIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            show: []
        },
        components: {
            list: HmiStaffList,
            create: HmiStaffCreate,
            edit: HmiStaffEdit,
            show: undefined,
        },
        showInMenu: false, // Grouped under devices
        menuOrder: 7
    },

    lockers: {
        name: 'lockers',
        recordRepresentation: 'number',
        route: '/lockers',
        label: 'resources.lockers.name',
        icon: LockerIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            show:[]
        },
        components: {
            list: LockerList,
            create: LockerCreate,
            edit: LockerEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 7
    },
    'locker-types': {
        name: 'locker-types',
        recordRepresentation: 'name',
        route: '/locker-types',
        label: 'resources.locker-types.name',
        icon: LockerTypeIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            create: ['SuperAdmin', 'GroupAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin'],
            delete: ['SuperAdmin'],
            show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist']
        },
        components: {
            list: LockerTypeList,
            create: LockerTypeCreate,
            edit: LockerTypeEdit,
            show: undefined,
        },
        showInMenu: false, // Grouped under admin-options
        menuOrder: 9
    },

    'locker-assignments': {
        name: 'locker-assignments',
        recordRepresentation: 'id',
        route: '/locker-assignments',
        label: 'resources.locker-assignments.name',
        icon: AssignmentIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            show:[]
        },
        components: {
            list: LockerAssignmentList,
            create: LockerAssignmentCreate,
            edit: LockerAssignmentEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 10
    },
    'hmi-simulator': {
        name: 'hmi-simulator',
        recordRepresentation: 'name',
        route: '/hmi-simulator',
        label: 'resources.hmi-simulator.name',
        icon: HMIIcon,
        permissions: {
            access: ['SuperAdmin', 'Developer'],
            list: ['SuperAdmin', 'Developer'],
            create: ['SuperAdmin', 'Developer'],
            edit: ['SuperAdmin', 'Developer'],
            delete: ['SuperAdmin', 'Developer'],
            show:[]
        },
        components: {
            list: HMISimulator,
            create: undefined,
            edit: undefined,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 11
    },

    // Grouped pages
    'admin-options': {
        name: 'admin-options',
        recordRepresentation: 'name',
        route: '/admin-options',
        label: 'resources.adminOptions.name',
        icon: SettingsIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin'],
            list: ['SuperAdmin', 'GroupAdmin'],
            create: [],
            edit: [],
            delete: [],
            show: []
        },
        components: {
            list: AdminOptionsPage,
            create: undefined,
            edit: undefined,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 1
    },

    'devices': {
        name: 'devices',
        recordRepresentation: 'name',
        route: '/devices',
        label: 'resources.devices.name',
        icon: DevicesIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            create: [],
            edit: [],
            delete: [],
            show: []
        },
        components: {
            list: DevicesPage,
            create: undefined,
            edit: undefined,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 2
    },
};

// Mantener compatibilidad con función anterior
export const hasPermission = (resource: string, action: string, userRole?: string) => {
    return hasResourcePermission(resource, action);
};

export default resourcesConfig;